// ロールベース権限制御カスタムフック
// Settings画面とアプリケーション全体での権限管理

import { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { UserRole, UserPermissions, DEFAULT_PERMISSIONS } from '../types/userManagement';
import userManagementService from '../services/userManagementService';

// 権限コンテキスト
interface PermissionContextType {
  currentUser: {
    id: string;
    role: UserRole;
    permissions: UserPermissions;
  } | null;
  hasPermission: (permission: keyof UserPermissions) => boolean;
  hasAnyPermission: (permissions: (keyof UserPermissions)[]) => boolean;
  hasAllPermissions: (permissions: (keyof UserPermissions)[]) => boolean;
  canAccessSettings: boolean;
  isLoading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | null>(null);

// 権限プロバイダー
interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    role: UserRole;
    permissions: UserPermissions;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // 実際の実装では、認証されたユーザーのIDを取得
      // 現在はデモ用にオーナーユーザーを使用
      const users = await userManagementService.getUsers();
      const ownerUser = users.find(user => user.access.role === 'owner');
      
      if (ownerUser) {
        setCurrentUser({
          id: ownerUser.id,
          role: ownerUser.access.role,
          permissions: ownerUser.access.permissions,
        });
      } else {
        // フォールバック: 開発環境では管理者権限を使用
        setCurrentUser({
          id: 'demo-user',
          role: 'owner',
          permissions: DEFAULT_PERMISSIONS.owner,
        });
      }
    } catch (err) {
      setError('権限情報の取得に失敗しました');
      console.error('Error loading user permissions:', err);
      
      // エラー時のフォールバック: 開発環境では管理者権限を使用
      setCurrentUser({
        id: 'fallback-user',
        role: 'owner',
        permissions: DEFAULT_PERMISSIONS.owner,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasPermission = (permission: keyof UserPermissions): boolean => {
    if (!currentUser) return false;
    return currentUser.permissions[permission] === true;
  };

  const hasAnyPermission = (permissions: (keyof UserPermissions)[]): boolean => {
    if (!currentUser) return false;
    return permissions.some(permission => currentUser.permissions[permission] === true);
  };

  const hasAllPermissions = (permissions: (keyof UserPermissions)[]): boolean => {
    if (!currentUser) return false;
    return permissions.every(permission => currentUser.permissions[permission] === true);
  };

  const canAccessSettings = hasPermission('canAccessSettings');

  const refreshPermissions = async () => {
    await loadCurrentUser();
  };

  const contextValue: PermissionContextType = {
    currentUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessSettings,
    isLoading,
    error,
    refreshPermissions,
  };

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// 権限フック
// eslint-disable-next-line react-refresh/only-export-components
export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// 特定権限チェックフック
// eslint-disable-next-line react-refresh/only-export-components
export const usePermission = (permission: keyof UserPermissions): boolean => {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
};

// 複数権限チェックフック
// eslint-disable-next-line react-refresh/only-export-components
export const useAnyPermission = (permissions: (keyof UserPermissions)[]): boolean => {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissions);
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAllPermissions = (permissions: (keyof UserPermissions)[]): boolean => {
  const { hasAllPermissions } = usePermissions();
  return hasAllPermissions(permissions);
};

// ロール別権限チェック
// eslint-disable-next-line react-refresh/only-export-components
export const useRolePermissions = () => {
  const { currentUser } = usePermissions();

  const isOwner = currentUser?.role === 'owner';
  const isManager = currentUser?.role === 'manager';
  const isStaff = currentUser?.role === 'staff';
  const isAdmin = isOwner; // 互換性のため
  const isGeneral = isStaff; // 互換性のため

  const canManageUsers = isOwner;
  const canManageSettings = isOwner || isManager;
  const canViewReports = isOwner || isManager;
  const canManageCustomers = isOwner || isManager || isStaff;
  const canCreateReservations = isOwner || isManager || isStaff;

  return {
    currentUser,
    isOwner,
    isManager,
    isStaff,
    isAdmin,
    isGeneral,
    canManageUsers,
    canManageSettings,
    canViewReports,
    canManageCustomers,
    canCreateReservations,
  };
};

// Settings画面専用権限フック
// eslint-disable-next-line react-refresh/only-export-components
export const useSettingsPermissions = () => {
  const { hasPermission, currentUser } = usePermissions();

  return {
    // アカウント管理
    canViewAccountSettings: hasPermission('canAccessSettings'),
    canEditAccountSettings: hasPermission('canAccessSettings'),

    // ユーザー管理
    canViewUsers: hasPermission('canViewUsers'),
    canManageUsers: hasPermission('canManageUsers'),
    canInviteUsers: hasPermission('canInviteUsers'),

    // 店舗設定
    canViewStoreSettings: hasPermission('canViewStoreSettings'),
    canEditStoreSettings: hasPermission('canEditStoreSettings'),

    // システム設定
    canManageIntegrations: hasPermission('canManageIntegrations'),
    canExportData: hasPermission('canExportData'),

    // 現在のユーザー情報
    currentUserId: currentUser?.id,
    currentRole: currentUser?.role,
  };
};

// 権限が不足している場合のコンポーネント
interface PermissionGateProps {
  permission: keyof UserPermissions | (keyof UserPermissions)[];
  fallback?: ReactNode;
  requireAll?: boolean;
  children: ReactNode;
}

export const PermissionGate: React.FC<PermissionGateProps> = ({ 
  permission, 
  fallback = null, 
  requireAll = false,
  children 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (Array.isArray(permission)) {
    hasAccess = requireAll 
      ? hasAllPermissions(permission)
      : hasAnyPermission(permission);
  } else {
    hasAccess = hasPermission(permission);
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

// ロール別ゲートコンポーネント
interface RoleGateProps {
  roles: UserRole | UserRole[];
  fallback?: ReactNode;
  children: ReactNode;
}

export const RoleGate: React.FC<RoleGateProps> = ({ roles, fallback = null, children }) => {
  const { currentUser } = usePermissions();

  if (!currentUser) {
    return <>{fallback}</>;
  }

  const allowedRoles = Array.isArray(roles) ? roles : [roles];
  const hasAccess = allowedRoles.includes(currentUser.role);

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
export default usePermissions;